// src/components/ecommerce/HeroSection.jsx - Add hover pause functionality
import React, { useState, useEffect } from 'react';
import { 
  ArrowRight,
  ShoppingBag,
  Star,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Zap,
  Truck,
  Shield,
  Phone,
  Mail,
  MapPin,
  Clock,
  Award,
  Users,
  TrendingUp,
  Gift,
  Percent,
  Tag
} from 'lucide-react';

// Icon mapping for trust indicators
const ICON_MAP = {
  'Truck': Truck,
  'Shield': Shield,
  'Award': Award,
  'Users': Users,
  'TrendingUp': TrendingUp,
  'Gift': Gift,
  'Percent': Percent,
  'Tag': Tag,
  'Zap': Zap,
  'Clock': Clock,
  'Phone': Phone,
  'Mail': Mail,
  'MapPin': MapPin
};

export const HeroSection = ({ 
  settings = {},
  styling = {},
  onCTAClick,
  className = ''
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHovering, setIsHovering] = useState(false); // Add hover state

  // Handle both legacy (settings.hero) and new (direct settings) data structures
  const heroData = settings.hero || settings;

  // Provide fallbacks for missing data
  const processedHeroData = {
    type: heroData.heroType || heroData.type || 'single',
    title: heroData.title || 'Welcome to Our Store',
    subtitle: heroData.subtitle || 'Discover amazing products at unbeatable prices',
    description: heroData.description || 'Shop the latest trends and find everything you need in one place.',
    ctaText: heroData.ctaText || 'Shop Now',
    ctaSecondaryText: heroData.ctaSecondaryText || 'Learn More',
    backgroundImage: heroData.backgroundImage || '',
    backgroundVideo: heroData.backgroundVideo || '',
    textColor: heroData.textColor || '#ffffff',
    overlayOpacity: heroData.overlayOpacity || 0.4,
    textPosition: heroData.textPosition || 'center',
    autoplay: heroData.autoplay !== false,
    slideDuration: heroData.slideDuration || 5000,
    showTrustIndicators: heroData.showTrustIndicators !== false,
    features: heroData.trustIndicators || heroData.features || [
      { icon: 'Truck', text: 'Shipping' },
      { icon: 'Shield', text: 'Secure Payment' },
      { icon: 'Award', text: 'Quality Guaranteed' }
    ],
    slides: heroData.slides || []
  };
  const heroFontSource =
    styling.fontFamily ||
    heroData.fontFamily ||
    'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
  const heroFontFamily = heroFontSource.includes(',') ? heroFontSource : `"${heroFontSource}", sans-serif`;

  // Determine which slides to use based on type and available data
  const slides = processedHeroData.slides.length > 0 
    ? processedHeroData.slides.filter(slide => slide.active !== false)
    : [processedHeroData];

  const currentSlideData = slides[currentSlide] || processedHeroData;

  // Auto-advance slides for carousel - Updated with hover pause
  useEffect(() => {
    if (processedHeroData.type === 'carousel' && 
        slides.length > 1 && 
        isPlaying && 
        processedHeroData.autoplay && 
        !isHovering) { // Pause when hovering
      
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % slides.length);
      }, processedHeroData.slideDuration);

      return () => clearInterval(interval);
    }
  }, [currentSlide, isPlaying, slides.length, processedHeroData.autoplay, processedHeroData.slideDuration, processedHeroData.type, isHovering]);

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Add hover handlers
  const handleMouseEnter = () => {
    if (processedHeroData.type === 'carousel' && slides.length > 1) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (processedHeroData.type === 'carousel' && slides.length > 1) {
      setIsHovering(false);
    }
  };

  // Video background hero
  if (processedHeroData.type === 'video' && currentSlideData.backgroundVideo) {
    return (
      <section 
        className={`relative h-[70vh] overflow-hidden ${className}`}
        style={{ fontFamily: heroFontFamily }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <video
          autoPlay
          muted
          loop
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={currentSlideData.backgroundVideo} type="video/mp4" />
        </video>

        {/* Overlay */}
        <div 
          className="absolute inset-0"
          style={{ 
            backgroundColor: `rgba(0, 0, 0, ${currentSlideData.overlayOpacity || 0.4})` 
          }}
        />

        {/* Content */}
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className={`max-w-2xl ${
              currentSlideData.textPosition === 'center' ? 'mx-auto text-center' :
              currentSlideData.textPosition === 'right' ? 'ml-auto text-right' :
              'text-left'
            }`}>
              <h1 
                className="text-4xl md:text-6xl font-bold mb-4"
                style={{ color: currentSlideData.textColor || '#ffffff' }}
              >
                {currentSlideData.title}
              </h1>
              
              {currentSlideData.subtitle && (
                <p 
                  className="text-xl md:text-2xl mb-6"
                  style={{ color: currentSlideData.textColor || '#ffffff' }}
                >
                  {currentSlideData.subtitle}
                </p>
              )}
              
              {currentSlideData.description && (
                <p 
                  className="text-lg mb-8 opacity-90"
                  style={{ color: currentSlideData.textColor || '#ffffff' }}
                >
                  {currentSlideData.description}
                </p>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onCTAClick}
                  className="px-8 py-4 text-white rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                  style={{ backgroundColor: styling.primaryColor || '#3b82f6' }}
                >
                  <ShoppingBag className="h-5 w-5" />
                  {currentSlideData.ctaText}
                </button>
                
                {currentSlideData.ctaSecondaryText && (
                  <button className="px-8 py-4 border-2 border-white border-opacity-30 text-white rounded-lg font-semibold text-lg transition-all duration-200 hover:bg-white hover:bg-opacity-10">
                    {currentSlideData.ctaSecondaryText}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        {processedHeroData.showTrustIndicators && processedHeroData.features.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-90 backdrop-blur-sm py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-center gap-8 text-sm text-gray-600 flex-wrap">
                {processedHeroData.features.map((feature, index) => {
                  const IconComponent = ICON_MAP[feature.icon] || Award;
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <IconComponent className="h-4 w-4" />
                      <span>{feature.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }

  // Split layout hero
  if (processedHeroData.type === 'split') {
    return (
      <section 
        className={`relative bg-white ${className}`}
        style={{ fontFamily: heroFontFamily }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh]">
            {/* Content Side */}
            <div className="flex items-center p-8 lg:p-12">
              <div className="max-w-lg">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  {currentSlideData.title}
                </h1>
                
                {currentSlideData.subtitle && (
                  <p className="text-xl text-gray-600 mb-6">
                    {currentSlideData.subtitle}
                  </p>
                )}
                
                {currentSlideData.description && (
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    {currentSlideData.description}
                  </p>
                )}
                
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <button
                    onClick={onCTAClick}
                    className="px-8 py-4 text-white rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                    style={{ backgroundColor: styling.primaryColor || '#3b82f6' }}
                  >
                    <ShoppingBag className="h-5 w-5" />
                    {currentSlideData.ctaText}
                  </button>
                  
                  {currentSlideData.ctaSecondaryText && (
                    <button className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold text-lg transition-all duration-200 hover:border-gray-400 hover:bg-gray-50">
                      {currentSlideData.ctaSecondaryText}
                    </button>
                  )}
                </div>

                {/* Trust Indicators */}
                {processedHeroData.showTrustIndicators && processedHeroData.features.length > 0 && (
                  <div className="grid grid-cols-1 gap-3">
                    {processedHeroData.features.slice(0, 3).map((feature, index) => {
                      const IconComponent = ICON_MAP[feature.icon] || Award;
                      return (
                        <div key={index} className="flex items-center gap-3">
                          <IconComponent className="h-5 w-5 text-gray-600" />
                          <span className="text-sm text-gray-600">{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Image Side */}
            <div className="relative">
              {currentSlideData.backgroundImage ? (
                <img
                  src={currentSlideData.backgroundImage}
                  alt={currentSlideData.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div 
                  className="w-full h-full flex items-center justify-center"
                  style={{ backgroundColor: (styling.accentColor || '#f59e0b') + '20' }}
                >
                  <ShoppingBag className="h-32 w-32 text-gray-400" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default: Single or Carousel Hero - Updated with hover events
  return (
    <section 
      className={`relative overflow-hidden ${className}`}
      style={{
        backgroundImage: currentSlideData.backgroundImage 
          ? `url(${currentSlideData.backgroundImage})` 
          : 'none',
        backgroundColor: !currentSlideData.backgroundImage 
          ? (styling.backgroundColor || '#f3f4f6') 
          : 'transparent',
        minHeight: '100vh',
        fontFamily: heroFontFamily
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background Overlay */}
      {currentSlideData.backgroundImage && (
        <div 
          className="absolute inset-0"
          style={{ 
            backgroundColor: `rgba(0, 0, 0, ${currentSlideData.overlayOpacity || 0.4})` 
          }}
        />
      )}

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 pt-28">
        <div className="w-full max-w-7xl mx-auto text-center mt-8 md:mt-0">
          <div className={`max-w-3xl ${
            currentSlideData.textPosition === 'center' ? 'mx-auto text-center' :
            currentSlideData.textPosition === 'right' ? 'ml-auto text-right' :
            'text-left'
          }`}>
            <h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ 
                color: currentSlideData.backgroundImage 
                  ? (currentSlideData.textColor || '#ffffff')
                  : (styling.textColor || '#1f2937')
              }}
            >
              {currentSlideData.title}
            </h1>
            
            {currentSlideData.subtitle && (
              <p 
                className="text-lg md:text-xl mb-6"
                style={{ 
                  color: currentSlideData.backgroundImage 
                    ? (currentSlideData.textColor || '#ffffff')
                    : (styling.textColor || '#6b7280')
                }}
              >
                {currentSlideData.subtitle}
              </p>
            )}
            
            {currentSlideData.description && (
              <p 
                className="text-lg mb-8 opacity-90 max-w-3xl"
                style={{ 
                  color: currentSlideData.backgroundImage 
                    ? (currentSlideData.textColor || '#ffffff')
                    : (styling.textColor || '#6b7280')
                }}
              >
                {currentSlideData.description}
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
              <button
                onClick={onCTAClick}
                className="px-6 py-2 text-white rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                style={{ backgroundColor: styling.primaryColor || '#3b82f6' }}
              >
                <ShoppingBag className="h-5 w-5" />
                {currentSlideData.ctaText}
              </button>
              
              {currentSlideData.ctaSecondaryText && (
                <button 
                  className="px-6 py-2 border-2 rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105"
                  style={{
                    borderColor: currentSlideData.backgroundImage 
                      ? (currentSlideData.textColor || '#ffffff') 
                      : (styling.primaryColor || '#3b82f6'),
                    color: currentSlideData.backgroundImage 
                      ? (currentSlideData.textColor || '#ffffff') 
                      : (styling.primaryColor || '#3b82f6')
                  }}
                >
                  {currentSlideData.ctaSecondaryText}
                </button>
              )}
            </div>

            {/* Trust Indicators - Inline Style */}
            {processedHeroData.showTrustIndicators && processedHeroData.features.length > 0 && (
              <div className="flex items-center justify-center gap-8 text-sm flex-wrap">
                {processedHeroData.features.map((feature, index) => {
                  const IconComponent = ICON_MAP[feature.icon] || Award;
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <IconComponent 
                        className="h-4 w-4" 
                        style={{ 
                          color: currentSlideData.backgroundImage 
                            ? (currentSlideData.textColor || '#ffffff')
                            : (styling.textColor || '#6b7280')
                        }}
                      />
                      <span 
                        style={{ 
                          color: currentSlideData.backgroundImage 
                            ? (currentSlideData.textColor || '#ffffff')
                            : (styling.textColor || '#6b7280')
                        }}
                      >
                        {feature.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Carousel Controls */}
      {processedHeroData.type === 'carousel' && slides.length > 1 && (
        <>
          {/* Navigation Arrows - Enhanced with hover visibility */}
          <button
            onClick={prevSlide}
            className={`absolute left-4 top-1/2 transform -translate-y-1/2 p-2 bg-black rounded-full transition-all duration-200 ${
              isHovering ? 'bg-opacity-70 hover:bg-opacity-90' : 'bg-opacity-50 hover:bg-opacity-70'
            } text-white`}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button
            onClick={nextSlide}
            className={`absolute right-4 top-1/2 transform -translate-y-1/2 p-2 bg-black rounded-full transition-all duration-200 ${
              isHovering ? 'bg-opacity-70 hover:bg-opacity-90' : 'bg-opacity-50 hover:bg-opacity-70'
            } text-white`}
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentSlide 
                    ? 'bg-white scale-125'
                    : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                }`}
              />
            ))}
          </div>

          {/* Play/Pause Button - Updated with better hover states */}
          {processedHeroData.autoplay && (
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className={`absolute bottom-8 right-8 p-2 bg-black rounded-full transition-all duration-200 text-white ${
                isHovering ? 'bg-opacity-70 hover:bg-opacity-90' : 'bg-opacity-50 hover:bg-opacity-70'
              }`}
              title={isPlaying ? 'Pause slideshow' : 'Resume slideshow'}
            >
              {isPlaying && !isHovering ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </button>
          )}

          {/* Hover Pause Indicator */}
          {isHovering && isPlaying && processedHeroData.autoplay && (
            <div className="absolute top-4 right-4 px-3 py-1 bg-black bg-opacity-70 text-white text-sm rounded-lg transition-opacity duration-200">
              <div className="flex items-center gap-2">
                <Pause className="h-3 w-3" />
                <span>Paused</span>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};
